import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { listAgents, type AgentBrief } from '@/features/agents/agents.api';

/** React Query key for the agent-directory list. */
export const agentsQueryKey = ['agents', 'list'] as const;

/**
 * React Query hook that fetches the full agent directory once per session and
 * returns a `Map<UUID, AgentBrief>` for O(1) name-lookup by `sender_agent_id`.
 *
 *   - `staleTime: 5min` — agents rarely change mid-session; no refetch needed.
 *   - `select` transforms the raw array into the Map so callers do:
 *       `const agent = byId.get(message.sender_agent_id)`
 *     without any extra iteration.
 *
 * Usage in `MessageBubble`:
 *   ```ts
 *   const { data: byId } = useAgentsById();
 *   const agentName = byId?.get(message.sender_agent_id)?.display_name;
 *   ```
 */
export function useAgentsById(): UseQueryResult<Map<string, AgentBrief>, Error> {
  return useQuery<AgentBrief[], Error, Map<string, AgentBrief>>({
    queryKey: agentsQueryKey,
    queryFn: listAgents,
    staleTime: 5 * 60 * 1000,
    select: (data) => new Map(data.map((a) => [a.id, a])),
  });
}
